import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Button, Dropdown, Menu, Typography, Space,
  Empty, Spin, message, Modal, Tag, Tooltip
} from 'antd';
import {
  MoreOutlined, EditOutlined, DeleteOutlined, CopyOutlined,
  EyeOutlined, DownloadOutlined, PlusOutlined, QrcodeOutlined
} from '@ant-design/icons';
import { deleteTicketTemplateApi, duplicateTicketTemplateApi, getTicketTemplateApi, getTicketTemplateByIdApi } from '../../../redux/api/api';

const { Title, Text } = Typography;


const BadgeTemplatesGallery = ({ onEditTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await getTicketTemplateApi();
      //console.log("data", data)
      if (data?.success) {
        setTemplates(data.templates || []);
      } else {
        message.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (template) => {
    const hide = message.loading(`Loading "${template.name}"...`, 0);
    try {
      const res = await getTicketTemplateByIdApi(template._id);
      if (res?.data?.success) {
        // Call the parent function to open the modal
        onEditTemplate(res.data.data.template);
      } else {
        message.error(res?.data?.message || 'Failed to load template for edit');
      }
    } catch (err) {
      console.error('Edit load error:', err);
      message.error('Failed to load template for edit');
    } finally {
      hide();
    }
  };
  const handleDuplicate = async (template) => {
    const hide = message.loading(`Duplicating "${template.name}"...`, 0);
    try {
      const res = await duplicateTicketTemplateApi(template._id);
      if (res?.data?.success) {
        // if API returns the new template, prepend/append it, otherwise refetch
        const newTemplate = res.data.template;
        if (newTemplate) {
          setTemplates((prev) => [newTemplate, ...prev]);
        } else {
          // fallback: refetch full list
          fetchTemplates();
        }
        message.success(`Template "${template.name}" duplicated`);
      } else {
        message.error(res?.data?.message || 'Duplicate failed');
      }
    } catch (err) {
      console.error('Duplicate error:', err);
      message.error('Duplicate failed');
    } finally {
      hide();
    }
  };

  const handleDelete = (template) => {
    //console.log(template)
    setSelectedTemplate(template);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    const hide = message.loading(`Deleting "${selectedTemplate.name}"...`, 0);

    try {
      const res = await deleteTicketTemplateApi(selectedTemplate._id);

      if (res?.data?.success) {
        // Use functional update to ensure we're working with latest state
        setTemplates((prev) => prev.filter(t => t._id !== selectedTemplate._id));
        message.success(`Template "${selectedTemplate.name}" deleted`);
      } else {
        message.error(res?.data?.message || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Delete error:', err);
      message.error('Failed to delete template');
    } finally {
      // Always clean up
      setDeleteModalVisible(false);
      setSelectedTemplate(null);
      hide();
    }
  };
  const handlePreview = (template) => {
    message.info(`Previewing: ${template.name}`);
    // Open preview modal or navigate to preview page
  };

  const handleExport = (template) => {
    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `${template.name.replace(/\s+/g, '_')}_template.json`;
    link.click();
    message.success('Template exported');
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return new Date(date).toLocaleDateString();
  };

  const renderBadgePreview = (template) => {
    const hasQR = template.fields?.some(f => f.type === 'qr_code' || f.type === 'qrcode');

    return (
      <div
        style={{
          width: '100%',
          height: 200,
          backgroundColor: template.backgroundColor || '#FFFFFF',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          position: 'relative',
          border: '1px solid #e8e8e8'
        }}
      >
        {/* Mini preview layout */}
        <div style={{
          width: '60%',
          height: 20,
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
          marginBottom: 12
        }} />

        <div style={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.05)',
          marginBottom: 12,
          border: '2px dashed rgba(0,0,0,0.2)'
        }} />

        <div style={{
          width: '80%',
          height: 16,
          backgroundColor: 'rgba(0,0,0,0.08)',
          borderRadius: 4,
          marginBottom: 8
        }} />

        <div style={{
          width: '60%',
          height: 12,
          backgroundColor: 'rgba(0,0,0,0.06)',
          borderRadius: 4,
          marginBottom: 12
        }} />

        {hasQR && (
          <div style={{
            position: 'absolute',
            bottom: 12,
            right: 12
          }}>
            <QrcodeOutlined style={{ fontSize: 24, opacity: 0.3 }} />
          </div>
        )}
      </div>
    );
  };

  const getMenuItems = (template) => [
    {
      key: 'preview',
      icon: <EyeOutlined />,
      label: 'Preview',
      onClick: () => handlePreview(template)
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit',
      onClick: () => handleEdit(template)
    },
    {
      key: 'duplicate',
      icon: <CopyOutlined />,
      label: 'Duplicate',
      onClick: () => handleDuplicate(template)
    },
    // {
    //   key: 'export',
    //   icon: <DownloadOutlined />,
    //   label: 'Export',
    //   onClick: () => handleExport(template)
    // },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: () => handleDelete(template)
    }
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Spin size="large" tip="Loading templates..." />
      </div>
    );
  }

  return (
    <div style={{
      padding: 24,
      backgroundColor: '#f5f5f5',
      minHeight: '50vh'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* <Title level={3} style={{ margin: 0 }}>Badges</Title> */}
        {/* <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => window.location.href = '/badges/create'}
        >
          Create Badge
        </Button> */}
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card>
          <Empty
            description={
              <Space direction="vertical" size="small">
                <Text>No badge templates found</Text>
                <Text type="secondary">Create your first badge template to get started</Text>
              </Space>
            }
          >
            {/* <Button type="primary" icon={<PlusOutlined />}>
              Create Badge Template
            </Button> */}
          </Empty>
        </Card>
      ) : (
        <Row gutter={[24, 24]}>
          {templates.map((template) => (
            <Col xs={24} sm={12} md={8} lg={6} key={template._id}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  borderRadius: 8,
                  overflow: 'hidden'
                }}
                bodyStyle={{ padding: 0 }}
                cover={renderBadgePreview(template)}
                actions={[
                  <Tooltip title="Preview">
                    <EyeOutlined
                      key="preview"
                      onClick={() => handlePreview(template)}
                    />
                  </Tooltip>,
                  <Tooltip title="Edit">
                    <EditOutlined
                      key="edit"
                      onClick={() => handleEdit(template)}
                    />
                  </Tooltip>,
                  <Dropdown
                    menu={{ items: getMenuItems(template) }}
                    trigger={['click']}
                  >
                    <MoreOutlined key="more" />
                  </Dropdown>
                ]}
              >
                <div style={{ padding: '12px 16px' }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <Title
                        level={5}
                        style={{ margin: 0 }}
                        ellipsis={{ rows: 1 }}
                      >
                        {template.name}
                      </Title>
                    </div>

                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {template.updatedAt
                        ? `Last modified ${getTimeAgo(template.updatedAt)}`
                        : `Created ${getTimeAgo(template.createdAt)}`
                      }
                    </Text>

                    {template.usageCount !== undefined && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Used {template.usageCount} time{template.usageCount !== 1 ? 's' : ''}
                      </Text>
                    )}
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Template"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedTemplate(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete "<strong>{selectedTemplate?.name}</strong>"?
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default BadgeTemplatesGallery;